import * as React from "react"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Loader2, Save, X } from "lucide-react"

function SmartForm({
  schema,
  defaultValues = {},
  fields,
  onSubmit,
  onCancel,
  isLoading = false,
  title,
  description,
  submitLabel = "Save",
  className,
}) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  })

  const onSubmitHandler = async (data) => {
    try {
      await onSubmit(data)
    } catch (error) {
      console.error("Form error:", error)
    }
  }

  return (
    <Card className={className}>
      {title && (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </CardHeader>
      )}
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <CardContent className="space-y-4">
          {fields.map((field) => (
            <FormField
              key={field.name}
              control={control}
              name={field.name}
              label={field.label}
              description={field.description}
              type={field.type}
              placeholder={field.placeholder}
              options={field.options}
              rules={field.rules}
              disabled={field.disabled}
              errors={errors}
            />
          ))}
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {submitLabel}
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}

function FormField({
  control,
  name,
  label,
  description,
  type = "text",
  placeholder,
  options,
  rules,
  disabled = false,
  errors,
}) {
  const error = errors[name]

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
        {label}
      </label>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => {
          switch (type) {
            case "select":
              return (
                <select
                  {...field}
                  disabled={disabled}
                  className={cn(
                    "flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    error && "border-destructive"
                  )}
                >
                  <option value="">{placeholder || "Select..."}</option>
                  {options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )
            case "textarea":
              return (
                <textarea
                  {...field}
                  disabled={disabled}
                  placeholder={placeholder}
                  className={cn(
                    "flex min-h-[80px] w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background",
                    "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    error && "border-destructive"
                  )}
                />
              )
            case "switch":
              return (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    {...field}
                    checked={field.value}
                    disabled={disabled}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-muted-foreground">{description}</span>
                </div>
              )
            case "number":
              return (
                <Input
                  {...field}
                  type="number"
                  disabled={disabled}
                  placeholder={placeholder}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  className={cn(error && "border-destructive")}
                />
              )
            default:
              return (
                <Input
                  {...field}
                  type={type}
                  disabled={disabled}
                  placeholder={placeholder}
                  className={cn(error && "border-destructive")}
                />
              )
          }
        }}
      />
      {description && type !== "switch" && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && (
        <p className="text-xs text-destructive">{error.message}</p>
      )}
    </div>
  )
}

// Form Builder Hook
function useFormBuilder(fields) {
  const defaultValues = React.useMemo(() => {
    const values = {}
    fields.forEach((field) => {
      values[field.name] = field.defaultValue ?? ""
    })
    return values
  }, [fields])

  return {
    defaultValues,
    fields,
  }
}

export { SmartForm, FormField, useFormBuilder }
